import { Field, ValueType } from "../../../models/workshop/common"
import { FormikValues } from "formik"
import * as Yup from "yup"
import { ApplicationInstance } from "../../../models/workshop/instance"
import { formTranslation } from "../../../locales/form"
import { ReleaseApplicationRequest } from "../../../models/workshop/api"
import { ApplicationTemplateVersion } from "../../../models/workshop/template"
import { MetaValues } from "../../../models/workshop/form"
import { safeJsonParse } from "../../../utility/common/safeJsonParse"

const tNamespace = "workshop:application."

const TAB_COUNT = 2

export const buildInitialValues = (fields: Field[]): FormikValues =>
    fields.reduce<FormikValues>((initialValues, field) => {
        switch (field.TypeValue.Case) {
            case ValueType.NUMBER:
                initialValues[field.Id] = Number(field.Value)
                break
            case ValueType.BOOLEAN:
                initialValues[field.Id] = field.Value === "true"
                break
            case ValueType.ARRAY:
                const parsedField = safeJsonParse<Record<string, unknown>[]>(field.Value, [])

                // объекты в массиве преобразуем обратно в строки для формы
                initialValues[field.Id] = parsedField.map(value =>
                    typeof value === "object" ? JSON.stringify(value, undefined, TAB_COUNT) : value
                )
                break
            case ValueType.SLOT:
                initialValues[field.Id] = field.Value || field.Id
                break
            default:
                initialValues[field.Id] = field.Value
        }

        return initialValues
    }, {})

export const objectFieldValidationSchema = Yup.string()
    .required(formTranslation.requiredField)
    .test("IsJson", `${tNamespace}is-json`, value => {
        if (!value) {
            return false
        }

        try {
            const field = JSON.parse(value)
            return typeof field === "object"
        } catch (e) {
            return false
        }
    })

export const getArrayValidationSchema = (type: string) => {
    switch (type) {
        case ValueType.OBJECT:
            return objectFieldValidationSchema
        default:
            return Yup.string().required(formTranslation.requiredField)
    }
}

export const buildFormValidationSchema = (fields: Field[]): Yup.ObjectSchema => {
    const schemaObject = fields.reduce<Record<string, Yup.Schema<unknown>>>((acc, field) => {
        switch (field.TypeValue.Case) {
            case ValueType.AGENT:
            case ValueType.PASSWORD:
            case ValueType.TEXT:
            case ValueType.STRING:
                acc[field.Id] = Yup.string().required(formTranslation.requiredField)
                break
            case ValueType.NUMBER:
                acc[field.Id] = Yup.number().required(formTranslation.requiredField)
                break
            case ValueType.BOOLEAN:
                acc[field.Id] = Yup.boolean().required(formTranslation.requiredField)
                break
            case ValueType.OBJECT:
                acc[field.Id] = objectFieldValidationSchema
                break
            case ValueType.ARRAY:
                acc[field.Id] = Yup.array().of(getArrayValidationSchema(field.TypeValue.Fields))
        }
        return acc
    }, {})

    return Yup.object().shape(schemaObject)
}

export const fillSettingsFields = (values: FormikValues, fields: Field[]): Field[] =>
    fields.map(field => {
        let value
        // специфичное условие, потому что оказалось, если в JSON.parse кинуть массив с одним элементом
        // и это стрингифайнутый объект, то возвращается объект, а не массив
        if (field.TypeValue.Case === ValueType.ARRAY && field.TypeValue.Fields === ValueType.OBJECT) {
            value = JSON.stringify(values[field.Id].map(JSON.parse))
        } else if (field.TypeValue.Case === ValueType.ARRAY) {
            value = JSON.stringify(values[field.Id])
        } else {
            value = values[field.Id]?.toString()
        }

        return {
            ...field,
            Value: value ?? ""
        }
    })

export const buildCreateOrUpdateRequest = (
    values: FormikValues,
    version: ApplicationTemplateVersion | ApplicationInstance,
    settingsFields: Field[],
    projectId: string,
    versionId: string,
    meta: MetaValues = {}
): ReleaseApplicationRequest => {
    const { title, ...valuesWithoutTitle } = values

    const settingsFieldsWithoutTitle = settingsFields.filter(setting => setting.Id !== "title")
    const filledSettingsFields = fillSettingsFields(valuesWithoutTitle, settingsFieldsWithoutTitle)

    return {
        Title: title,
        TemplateId: version.TemplateId,
        VersionId: versionId,
        Settings: {
            ...version.Settings,
            Fields: filledSettingsFields
        },
        Meta: {
            ...meta,
            customer_id: projectId,
            host: window.location.origin
        },
        ProjectId: projectId
    }
}
